import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { Prediction } from '../../types/predictions';
import { Theme } from '../../theme';
import { PriceDataPoint } from '../../utils/leaderboardScoring';
import MobileLeaderboard from './MobileLeaderboard';

const Overlay = styled.div<{ $visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1999;
  opacity: ${props => props.$visible ? 1 : 0};
  visibility: ${props => props.$visible ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
`;

const BottomSheet = styled.div<{ $open: boolean; $height: number }>`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2000;
  background: ${props => props.theme.surface};
  border-radius: 20px 20px 0 0;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.25);
  transform: translateY(${props => props.$open ? '0' : '100%'});
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  max-height: ${props => props.$height}vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const DragHandle = styled.div`
  display: flex;
  justify-content: center;
  padding: 12px 0 8px;
  cursor: grab;
  flex-shrink: 0;
  
  &:active {
    cursor: grabbing;
  }
`;

const HandleBar = styled.div`
  width: 40px;
  height: 4px;
  background: ${props => props.theme.border};
  border-radius: 2px;
`;

const SheetHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px 12px;
  border-bottom: 1px solid ${props => props.theme.border};
  flex-shrink: 0;
`;

const SheetTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: ${props => props.theme.text};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CloseButton = styled.button`
  background: ${props => props.theme.background};
  border: 1px solid ${props => props.theme.border};
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${props => props.theme.text};
  font-size: 18px;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.theme.hoverBackground};
    transform: scale(1.1);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const SheetContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 8px 16px;
  -webkit-overflow-scrolling: touch;
  
  /* Hide scrollbar but keep functionality */
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.border};
    border-radius: 2px;
  }
`;

// Floating trigger button
const FloatingButton = styled.button<{ $visible: boolean }>`
  position: fixed;
  bottom: 90px; /* Above bottom nav */
  right: 16px;
  z-index: 1500;
  background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%);
  border: none;
  border-radius: 50px;
  padding: 12px 20px;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 4px 15px rgba(255, 165, 0, 0.4);
  cursor: pointer;
  transition: all 0.3s ease;
  opacity: ${props => props.$visible ? 1 : 0};
  transform: ${props => props.$visible ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.9)'};
  pointer-events: ${props => props.$visible ? 'auto' : 'none'};
  
  &:hover {
    transform: translateY(-2px) scale(1.05);
    box-shadow: 0 6px 20px rgba(255, 165, 0, 0.5);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const ButtonIcon = styled.span`
  font-size: 18px;
`;

const ButtonText = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: #1a1a1a;
  white-space: nowrap;
`;

const MethodologyLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 16px;
  margin: 8px 16px 16px;
  background: ${props => props.theme.background};
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  color: ${props => props.theme.text};
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.theme.surface};
    border-color: #FFD700;
  }
`;

interface LeaderboardBottomSheetProps {
  predictions: Prediction[];
  chartData: PriceDataPoint[];
  theme: Theme;
}

const LeaderboardBottomSheet: React.FC<LeaderboardBottomSheetProps> = ({
  predictions,
  chartData,
  theme
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showButton, setShowButton] = useState(true);
  const sheetRef = useRef<HTMLDivElement>(null);
  
  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);
  
  // Prevent body scroll when sheet is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  
  const handleOpen = () => {
    setIsOpen(true);
    setShowButton(false);
  };
  
  const handleClose = () => {
    setIsOpen(false);
    // Show button after animation completes
    setTimeout(() => setShowButton(true), 300);
  };

  return (
    <>
      {/* Floating trigger button */}
      <FloatingButton 
        $visible={showButton && !isOpen}
        onClick={handleOpen}
        aria-label="Open Leaderboard"
      >
        <ButtonIcon>🏆</ButtonIcon>
        <ButtonText>Leaderboard</ButtonText>
      </FloatingButton>
      
      {/* Overlay */}
      <Overlay 
        $visible={isOpen} 
        onClick={handleClose}
      />
      
      {/* Bottom Sheet */}
      <BottomSheet 
        ref={sheetRef}
        $open={isOpen}
        $height={75}
        theme={theme}
      >
        <DragHandle onClick={handleClose}>
          <HandleBar theme={theme} />
        </DragHandle>
        
        <SheetHeader theme={theme}>
          <SheetTitle theme={theme}>
            <span>🏆</span>
            Prediction Leaderboard
          </SheetTitle>
          <CloseButton onClick={handleClose} theme={theme}>
            ✕
          </CloseButton>
        </SheetHeader>
        
        <SheetContent theme={theme}>
          <MobileLeaderboard
            predictions={predictions}
            chartData={chartData}
            theme={theme}
            maxEntries={15}
            embedded={true}
          />
          <MethodologyLink href="/leaderboard" theme={theme}>
            📐 View Scoring Methodology
          </MethodologyLink>
        </SheetContent>
      </BottomSheet>
    </>
  );
};

export default LeaderboardBottomSheet;
